import { useTranslation } from 'react-i18next';
import { FolderIconClosed, FolderIconOpen } from '@/utils/icons';
import { RenameInput } from '../../RenameInput/RenameInput';
import { PendingSpinner } from '../../PendingSpinner/PendingSpinner';
import type { IFolderItemProps } from '../itemProps';
import { useFolderDnd } from '../hooks/useFolderDnd';
import styles from './DocumentGrid.module.css';

export interface IFolderCardProps extends IFolderItemProps {
  /** The card that navigates UP — the open-folder glyph and a muted label. A
   *  drop target like the table's back row, but never picked up or renamed. */
  isParent?: boolean;
}

/**
 * A subfolder of the current folder, or the "back" card to its parent. Same
 * card shape as a file so the grid reads as one surface; the caption's second
 * line says what it is where a file would show its date. Registers under the
 * table's namespaced dnd id, so the page's drop rules apply unchanged.
 */
export function FolderCard({
  folder, isParent = false, isRejected, isMenuTarget = false, rename, onOpen, onContextMenu,
}: IFolderCardProps) {
  const { t } = useTranslation();
  const isRenaming = Boolean(rename);
  // Same lock as the table row.
  const isSaving = Boolean(folder.saving);
  const locked = isRenaming || isSaving;

  // Same rules as the sidebar: system roots (and the way back) can receive
  // drops but are fixed where the server put them, so they can't be picked up.
  const { setRef, attributes, listeners, style, dropHighlight } =
    useFolderDnd(folder.id, isParent || Boolean(folder.isSystem) || locked, isSaving);
  const Glyph = isParent ? FolderIconOpen : FolderIconClosed;

  function open() {
    if (!locked) onOpen(folder.id);
  }

  return (
    <div
      ref={setRef}
      style={style}
      className={`${styles.card} ${isMenuTarget ? styles.cardSelected : ''} ${dropHighlight ? styles.dropTarget : ''} ${isRejected ? styles.dragRejected : ''} ${isSaving ? styles.cardSaving : ''}`}
      aria-busy={isSaving || undefined}
      onClick={open}
      onContextMenu={(e) => {
        if (isSaving) e.preventDefault();
        else if (!isParent) onContextMenu?.(e, folder.id);
      }}
      onKeyDown={(e) => {
        if (!locked && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          open();
        }
      }}
      {...attributes}
      {...listeners}
    >
      <span className={styles.preview} aria-hidden="true">
        <span className={isParent ? styles.glyphParent : styles.glyph}><Glyph size={44} /></span>
      </span>
      <span className={styles.caption}>
        <span className={`${styles.name} ${isParent ? styles.nameMuted : ''} ${isRenaming ? styles.ghost : ''}`}>
          {isParent ? t('documents.backToParent', { name: folder.name }) : folder.name}
          {isSaving && <PendingSpinner />}
        </span>
        <span className={`${styles.meta} ${isRenaming ? styles.ghost : ''}`}>
          {t(isParent ? 'documents.card.parent' : 'documents.card.folder')}
        </span>
        {rename && (
          <span className={styles.renameOverlay}>
            <RenameInput {...rename} />
          </span>
        )}
      </span>
    </div>
  );
}
